// Streaming chat client for the health assistant.
// Uses fetch + ReadableStream to consume the Server-Sent Events from POST /api/chat.
// Stateless on the backend — we send the full message history each request.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CHAT_URL = `${BACKEND_URL}/api/chat`;

// If no bytes arrive within this window, we treat the request as stuck and
// abort so the user gets a clear "please try again" error instead of a
// forever-loading UI. Applies both to the initial response headers and to
// gaps between streamed tokens.
const FIRST_BYTE_TIMEOUT_MS = 25000;   // 25s to receive HTTP response headers
const IDLE_STREAM_TIMEOUT_MS = 20000;  // 20s between streamed tokens

/**
 * @param {{ session_id: string, messages: Array<{role:'user'|'assistant', content:string}> }} body
 * @param {{ onDelta: (text:string)=>void, onDone: ()=>void, onError: (err:Error)=>void, signal?: AbortSignal }} handlers
 */
export async function streamChat(body, handlers) {
    const { onDelta, onDone, onError, signal } = handlers;
    // Combine caller's abort signal with our own timeout controller so either
    // can cancel the fetch cleanly.
    const timeoutController = new AbortController();
    const combinedSignal = mergeSignals(signal, timeoutController.signal);

    // First-byte timeout — abort if the server never returns headers.
    let firstByteTimer = setTimeout(() => {
        timeoutController.abort(new Error("first-byte-timeout"));
    }, FIRST_BYTE_TIMEOUT_MS);

    // Idle-stream timeout — refreshed on every received chunk.
    let idleTimer = null;
    const armIdleTimer = () => {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(() => {
            timeoutController.abort(new Error("idle-stream-timeout"));
        }, IDLE_STREAM_TIMEOUT_MS);
    };

    try {
        const res = await fetch(CHAT_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "text/event-stream",
            },
            body: JSON.stringify(body),
            signal: combinedSignal,
        });
        clearTimeout(firstByteTimer);
        firstByteTimer = null;

        if (!res.ok || !res.body) {
            let detail = "";
            try {
                detail = await res.text();
            } catch (readErr) {
                if (typeof console !== "undefined") {
                    console.debug("streamChat: could not read error body", readErr);
                }
            }
            throw new Error(`Chat request failed (${res.status}). ${detail || ""}`.trim());
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";
        armIdleTimer();

        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            armIdleTimer();
            buffer += decoder.decode(value, { stream: true });

            let sepIdx;
            while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
                const rawEvent = buffer.slice(0, sepIdx);
                buffer = buffer.slice(sepIdx + 2);
                const lines = rawEvent.split("\n");
                for (const line of lines) {
                    if (!line.startsWith("data:")) continue;
                    const dataStr = line.slice(5).trim();
                    if (!dataStr) continue;
                    let parsed;
                    try {
                        parsed = JSON.parse(dataStr);
                    } catch (parseErr) {
                        if (typeof console !== "undefined") {
                            console.debug("streamChat: skipping malformed SSE frame", parseErr);
                        }
                        continue;
                    }
                    if (parsed.error) {
                        throw new Error(parsed.error);
                    }
                    if (parsed.done) {
                        if (idleTimer) clearTimeout(idleTimer);
                        onDone && onDone();
                        return;
                    }
                    if (typeof parsed.delta === "string") {
                        onDelta && onDelta(parsed.delta);
                    }
                }
            }
        }
        if (idleTimer) clearTimeout(idleTimer);
        onDone && onDone();
    } catch (err) {
        if (firstByteTimer) clearTimeout(firstByteTimer);
        if (idleTimer) clearTimeout(idleTimer);
        if (err && err.name === "AbortError") {
            // If the abort came from our timeout controller, surface a friendly
            // message. If it came from the caller's signal (user navigated /
            // closed), stay silent.
            if (signal && signal.aborted) return;
            onError && onError(new Error("The connection took too long. Please check your internet and try again."));
            return;
        }
        onError && onError(err);
    }
}

// Combine two AbortSignals into a single one that fires when EITHER aborts.
function mergeSignals(a, b) {
    if (!a) return b;
    if (!b) return a;
    const controller = new AbortController();
    const forward = (source) => {
        if (source.aborted) controller.abort(source.reason);
        else source.addEventListener("abort", () => controller.abort(source.reason), { once: true });
    };
    forward(a);
    forward(b);
    return controller.signal;
}

export function newSessionId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
